import { BasePagination, HStack, IconButtonWithTooltip, PlusIcon, Text, VStack } from 'design-system';
import { mdiSourceBranch, mdiTrashCanOutline } from '@mdi/js';
import MdiIcon from '@mdi/react';
import { useGetSupplierByIdApi } from 'apis/supplier/use-get-supplier-by-id-api';
import { useGetRestaurantSuppliersApi } from 'apis/restaurant-suppliers';
import { BaseTable, BaseTableContainer, setHiddenColumns, useCustomTableCheckboxes } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  DateTimeCell,
  IDateTimeCell,
  TableHeader,
} from 'components/base-table/components';
import { BaseSortButton } from 'components/base-table/sorting';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CellProps } from 'react-table';
import { AssignRestaurantModalCell } from '../components/assign-restaurant-modal';
import {
  IRestaurantRelationFlagSwitchCell,
  RestaurantRelationFlagSwitch,
} from '../components/restaurant-relation-flag-switch';
import { IUnassignRestaurantModalCell, UnassignRestaurantModalCell } from '../components/unassign-restaurant-modal';
import { EntityMappingModalCell, IEntityMappingModalCell } from '../components/entity-mapping-modal';

export const SupplierRestaurantsScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const { supplierId } = useParams<{ supplierId: string }>();

  const { data: supplierData } = useGetSupplierByIdApi(supplierId);

  const { isLoading, isFetching, data } = useGetRestaurantSuppliersApi(parseInt(supplierId));
  const { pageCount, total, data: itemsData } = data ?? {};

  useCustomTableCheckboxes(itemsData);

  interface IColumnsAccessorData {
    id: number | string;
    restaurant: { id: number; name: string };
    favorite: { relationId: number; value: boolean };
    isGpoRelation: { relationId: number; value: boolean };
    createdAt: string;
    actions: (typeof itemsData)[0];
  }

  const columns = useMemo(
    () => [
      {
        Header: <TableHeader name='Restaurant' sortButtonComponent={<BaseSortButton sortBy='restaurantName' />} />,
        accessor: 'restaurant',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['restaurant']>) => (
          <Link to={`/app/restaurants/details/${value?.id}`}>
            <Text fontWeight='semibold' color='gray.600'>
              {value?.name}
            </Text>
          </Link>
        ),
      },
      {
        Header: <TableHeader name='Favorite' sortButtonComponent={<BaseSortButton sortBy='favorite' />} />,
        accessor: 'favorite',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['favorite']>) => (
          <CustomTableCell<IRestaurantRelationFlagSwitchCell>
            as={RestaurantRelationFlagSwitch}
            relationId={value?.relationId}
            field='favorite'
            value={value?.value}
          />
        ),
      },
      {
        Header: <TableHeader name='GPO relation' />,
        accessor: 'isGpoRelation',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['isGpoRelation']>) => (
          <CustomTableCell<IRestaurantRelationFlagSwitchCell>
            as={RestaurantRelationFlagSwitch}
            relationId={value?.relationId}
            field='isGpoRelation'
            value={value?.value}
          />
        ),
      },
      {
        Header: <TableHeader name='Created' sortButtonComponent={<BaseSortButton sortBy='createdAt' />} />,
        accessor: 'createdAt',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['createdAt']>) => (
          <CustomTableCell<IDateTimeCell> as={DateTimeCell} date={value} />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <HStack spacing='xs'>
            <CustomTableCell<IEntityMappingModalCell>
              as={EntityMappingModalCell}
              restaurantId={value?.restaurantId}
              trigger={
                <IconButtonWithTooltip
                  size='sm'
                  label='Map branches'
                  aria-label='map-branches-btn'
                  Icon={<MdiIcon path={mdiSourceBranch} size={0.8} />}
                  variant='unstyled'
                />
              }
            />
            <CustomTableCell<IUnassignRestaurantModalCell>
              as={UnassignRestaurantModalCell}
              relationId={value?.id}
              restaurantName={value?.restaurant?.name}
              trigger={
                <IconButtonWithTooltip
                  size='sm'
                  label='Unassign'
                  aria-label='unassign-restaurant-btn'
                  Icon={<MdiIcon path={mdiTrashCanOutline} size={0.8} />}
                  variant='unstyled'
                />
              }
            />
          </HStack>
        ),
      },
    ],
    []
  );

  const columnsAccessorData = useMemo(
    () =>
      itemsData?.map((item: (typeof itemsData)[0]): IColumnsAccessorData => {
        return {
          // BaseTable/@dnd-kit needs a defined, unique row id; an undefined id makes every
          // row report isDragging=true and render blank. The BE list response omits the
          // relation `id`, so fall back to the unique restaurant+supplier pair for the row key.
          id: item?.id ?? `${item?.restaurantId}-${item?.supplierId}`,
          restaurant: { id: item?.restaurantId, name: item?.restaurant?.name },
          favorite: { relationId: item?.id, value: item?.favorite },
          isGpoRelation: { relationId: item?.id, value: item?.isGpoRelation },
          createdAt: item?.createdAt,
          actions: item,
        };
      }),
    [itemsData]
  );

  return (
    <VStack spacing='sm' align='stretch'>
      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <VStack spacing='sm' align='start'>
              <HStack>
                <BaseTableTitle title={`Suppliers > ${supplierData?.data?.name ?? ''} > Restaurants`} />
                <BaseTableSpinner isFetching={isFetching} />
              </HStack>
            </VStack>
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <ClearFilterButton />
            <AssignRestaurantModalCell
              trigger={
                <IconButtonWithTooltip label='Assign Restaurant' aria-label='assign-restaurant-btn' Icon={<PlusIcon />} />
              }
            />
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          <BaseTable columns={columns} data={columnsAccessorData ?? []} initialHiddenColumns={setHiddenColumns([])} />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>
    </VStack>
  );
};
